export interface RealtimeMessage<T = Record<string, unknown>> {
  event: string;
  id: string;
  payload: T;
  actorId: string | null;
  projectId: string | null;
  companyId: string | null;
  correlationId: string;
  occurredAt: string;
}

export interface RealtimeNotification {
  id: string;
  type: string;
  title: string;
  body: string;
  url: string | null;
  data: Record<string, unknown>;
  createdAt: string;
}

export interface PresenceUser {
  userId: string;
  displayName: string;
}

export interface RealtimeOptions {
  /** Origin of the API, e.g. `http://localhost:3000` (no `/api` suffix). */
  url: string;
  token: string;
  autoConnect?: boolean;
  reconnectionDelayMs?: number;
  /**
   * Rotates the access token and resolves with the new one, or with `null` when
   * the session is gone. A socket has no response to read a 401 from, so an
   * expired token only ever shows up as a handshake the gateway refuses.
   */
  refreshToken?: () => Promise<string | null>;
}

/** Longest gap between attempts to recover a refused handshake. */
const MAX_RETRY_DELAY_MS = 30_000;

type Listener<T> = (payload: T) => void;

/** Live-run channels, relayed verbatim to whoever subscribed to the room. */
const DEPLOY_CHANNELS = ['deploy:log', 'deploy:step', 'deploy:status'] as const;

/** One chunk of a running pipeline's combined stdout and stderr. */
export interface DeployLogMessage {
  executionId: string;
  seq: number;
  text: string;
}

/** A step transition, or the end of a run. */
export interface DeployStatusMessage {
  executionId: string;
  status: string;
  exitCode?: number | null;
  error?: string | null;
  durationMs?: number;
  cancelRequested?: boolean;
  steps?: Array<{ stepId: string; name: string; status: string; exitCode?: number | null; durationMs?: number }>;
}

/**
 * Thin wrapper around the `/realtime` socket.io namespace.
 *
 * Subscriptions are remembered, so everything is re-joined automatically after
 * a reconnect — the caller never has to re-subscribe by hand.
 */
export class RealtimeClient {
  private socket: any = null;
  private readonly rooms = new Set<string>();
  private readonly listeners = new Map<string, Set<Listener<any>>>();
  private connected = false;
  /** True between `connect()` and `disconnect()` — i.e. we want a live socket. */
  private wanted = false;
  /** In-flight token rotation, awaited by a retry so it carries the new token. */
  private reauth: Promise<void> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retryDelayMs = 0;

  constructor(private options: RealtimeOptions) {}

  /** socket.io-client is an optional peer dependency, so it is imported lazily. */
  async connect(): Promise<void> {
    this.wanted = true;
    if (this.socket) return;

    const { io } = await import('socket.io-client');
    this.socket = io(`${this.options.url.replace(/\/$/, '')}/realtime`, {
      transports: ['websocket', 'polling'],
      // A callback is re-read on every attempt. An object literal would pin the
      // token the socket was built with, so a reconnect after the access token
      // expired would replay the dead one until the page was reloaded.
      auth: (cb: (data: { token: string }) => void) => cb({ token: this.options.token }),
      reconnectionDelay: this.options.reconnectionDelayMs ?? 1000,
      autoConnect: true,
    });

    this.socket.on('connect', () => {
      this.connected = true;
      this.clearRetry();
      this.retryDelayMs = 0;
      this.emitLocal('connection:open', undefined);
    });
    this.socket.on('disconnect', (reason: string) => {
      this.connected = false;
      this.emitLocal('connection:close', reason);
      // socket.io never reconnects on its own after the server hung up, and the
      // gateway hangs up on exactly one thing: a handshake it could not
      // authenticate. Left alone the socket stays dead for the rest of the page
      // — the state behind a "reconnecting" chip that never clears. Rotate the
      // token, which after a long spell in the background has simply expired,
      // and bring the socket back by hand.
      if (reason === 'io server disconnect') {
        void this.reauthorize();
        this.scheduleRetry();
      }
    });
    this.socket.on('connect_error', (error: Error) => this.emitLocal('connection:error', error));
    this.socket.on('unauthorized', (payload: { message: string }) => this.emitLocal('unauthorized', payload));
    // The transport opens before the server's async authentication finishes.
    // Rejoin only after its acknowledgement or the subscribe can arrive before
    // the server has created this socket's state and be silently refused.
    this.socket.on('connected', (payload: unknown) => {
      if (this.rooms.size) this.socket.emit('subscribe', { rooms: [...this.rooms] });
      this.emitLocal('ready', payload);
    });

    this.socket.on('event', (message: RealtimeMessage) => {
      this.emitLocal('event', message);
      this.emitLocal(`event:${message.event}`, message);
    });
    this.socket.on('notification', (payload: RealtimeNotification) => this.emitLocal('notification', payload));
    this.socket.on('presence:join', (payload: unknown) => this.emitLocal('presence:join', payload));
    this.socket.on('presence:leave', (payload: unknown) => this.emitLocal('presence:leave', payload));
    this.socket.on('typing', (payload: unknown) => this.emitLocal('typing', payload));

    // Deploy pipelines stream their output over dedicated channels rather than
    // the domain-event one: a run prints thousands of lines, none of which is a
    // domain event, and none of which anybody outside the open log view wants.
    for (const channel of DEPLOY_CHANNELS) {
      this.socket.on(channel, (payload: unknown) => this.emitLocal(channel, payload));
    }
  }

  disconnect(): void {
    this.wanted = false;
    this.clearRetry();
    this.retryDelayMs = 0;
    this.socket?.disconnect();
    this.socket = null;
    this.connected = false;
  }

  get isConnected(): boolean {
    return this.connected;
  }

  /** Updates the token and reconnects — call this after a token refresh. */
  async updateToken(token: string): Promise<void> {
    this.options.token = token;
    if (this.socket) {
      // `auth` is a callback reading `options.token`, so the reconnect below
      // picks the new token up without touching the socket's own state.
      this.clearRetry();
      this.retryDelayMs = 0;
      this.socket.disconnect();
      this.socket.connect();
    }
  }

  /**
   * Brings the socket back after the page was frozen.
   *
   * Mobile browsers stop the timers of a backgrounded tab, so a socket that
   * died while the app was away may still be waiting on a retry that is not
   * running. Returning to the foreground is the moment to check.
   */
  ensureConnected(): void {
    if (!this.wanted || this.connected || !this.socket) return;
    this.clearRetry();
    this.retryDelayMs = 0;
    void this.retryNow();
  }

  /**
   * Rotates the access token behind the socket. Concurrent callers share the
   * one attempt, and a failure is left to the retry timer — the usual cause is
   * being offline, which resolves itself.
   */
  private reauthorize(): Promise<void> {
    if (this.reauth) return this.reauth;
    const refresh = this.options.refreshToken;
    if (!refresh) return Promise.resolve();

    this.reauth = refresh()
      .then((token) => {
        if (token) this.options.token = token;
      })
      .catch(() => undefined)
      .finally(() => {
        this.reauth = null;
      });
    return this.reauth;
  }

  /** Backs off so a handshake that keeps being refused is not a hot loop. */
  private scheduleRetry(): void {
    if (!this.wanted || this.retryTimer) return;
    const base = this.options.reconnectionDelayMs ?? 1000;
    this.retryDelayMs = this.retryDelayMs ? Math.min(this.retryDelayMs * 2, MAX_RETRY_DELAY_MS) : base;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.retryNow();
    }, this.retryDelayMs);
  }

  private async retryNow(): Promise<void> {
    // Reconnecting before the replacement token lands would only be refused
    // again, and would burn a backoff step doing it.
    await this.reauth;
    if (!this.wanted || this.connected || !this.socket) return;
    this.socket.connect();
  }

  private clearRetry(): void {
    if (!this.retryTimer) return;
    clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  subscribe(rooms: string[]): void {
    for (const room of rooms) this.rooms.add(room);
    if (this.connected) this.socket?.emit('subscribe', { rooms });
  }

  unsubscribe(rooms: string[]): void {
    for (const room of rooms) this.rooms.delete(room);
    if (this.connected) this.socket?.emit('unsubscribe', { rooms });
  }

  setTyping(cardId: string, typing: boolean): void {
    this.socket?.emit('typing', { cardId, typing });
  }

  /**
   * The same signal addressed by room, so it serves a chat panel as well as a
   * card's comment thread. The server only relays it into a room this
   * connection has already subscribed to.
   */
  setTypingIn(room: string, typing: boolean): void {
    this.socket?.emit('typing', { room, typing });
  }

  async presence(room: string): Promise<PresenceUser[]> {
    if (!this.socket) return [];
    return new Promise((resolve) => {
      this.socket.emit('presence', { room }, (response: { users: PresenceUser[] }) => resolve(response?.users ?? []));
      setTimeout(() => resolve([]), 3000);
    });
  }

  /** `on('event')` for everything, or `on('event:CardMoved')` for one event. */
  on<T = unknown>(channel: string, listener: Listener<T>): () => void {
    const set = this.listeners.get(channel) ?? new Set();
    set.add(listener);
    this.listeners.set(channel, set);
    return () => set.delete(listener);
  }

  off(channel: string, listener: Listener<any>): void {
    this.listeners.get(channel)?.delete(listener);
  }

  private emitLocal(channel: string, payload: unknown): void {
    for (const listener of this.listeners.get(channel) ?? []) {
      try {
        listener(payload);
      } catch {
        // A broken subscriber must not stop the others.
      }
    }
  }
}
